import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AIStepLayout } from '../masterclass/ai/AIStepLayout';
import { useProgramCreation } from '../ProgramCreationContext';
import { API } from '../../../../api';
import DosenInput from '../../../../pages/new-designs/components/DosenInput';
import { useAppSelector } from '../../../../redux/hooks';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { events, attributeKeys } from '../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../utils/amplitudeProductTracking';

const MENTORSHIP_STEPS = ['Program name', 'Learning objectives', 'People'];

export function MentorshipProgramName() {
  const navigate = useNavigate();
  const { state, dispatch } = useProgramCreation();
  const { coId: coIdParam = '', orgId: orgIdParam = '', groupId: routeGroupId = '' } = useProgramContext();
  const orgIdFromStore = useAppSelector((s) => s.orgDetails.data?._id) || orgIdParam;
  const parentGroupId = useAppSelector((s) => s.orgDetails.data?.grpId) || routeGroupId;
  const [name, setName] = useState(state.mentorship.name || '');
  const [saving, setSaving] = useState(false);

  // Pick up the seed name from ProgramCreationFlow when entering this
  // flow on a design-stage program — the useState init above only fires
  // once, so a context update that arrives after mount (async fetch of
  // groupDetails) wouldn't otherwise hydrate the input. Only once: after
  // that the field is the admin's, so clearing it to type a new name
  // leaves it empty instead of putting the old name back.
  const seeded = useRef(Boolean(name.trim()));
  useEffect(() => {
    if (seeded.current || !state.mentorship.name) return;
    seeded.current = true;
    setName(state.mentorship.name);
  }, [state.mentorship.name]);

  const createOrUpdateGroup = async () => {
    let groupId = state.groupId;
    const formData = new FormData();

    if (!orgIdFromStore || !parentGroupId) {
      throw new Error('Missing workspace context to create the group');
    }

    if (!groupId) {
      const response = await API.createGroup({
        orgId: orgIdFromStore,
        name: name.trim(),
        parent: parentGroupId
      });
      groupId = response.data.groupDetails.grpId;

      // Program lands in the In Design column the moment it's created.
      trackWithBaseProps(events.programInDesign, {
        [attributeKeys.programName]: name.trim(),
        [attributeKeys.programModel]: 'Mentorship',
        [attributeKeys.adminProgramStatus]: 'In Design',
        [attributeKeys.programCreationFlow]: 'Manual',
        grpId: groupId
      });
    }

    formData.append('model', 'mentorship');
    formData.append('includeTask', 'true');
    formData.append('allowEditing', 'true');
    formData.append('mentee', 'mentee');
    formData.append('mentor', 'mentor');
    formData.append('name', name.trim());

    if (!groupId) {
      throw new Error('Failed to create mentorship group');
    }

    await API.updateGroupInfo({
      orgId: coIdParam || orgIdParam || orgIdFromStore,
      grpId: groupId,
      formData
    });

    const finalGroupId = groupId || '';
    dispatch({ type: 'SET_GROUP_ID', payload: finalGroupId });
    dispatch({ type: 'SET_BUILD_MODE', payload: 'manual' });
    dispatch({ type: 'UPDATE_MENTORSHIP', payload: { name: name.trim() } });
  };

  const handleNext = async () => {
    if (!name.trim()) return;

    setSaving(true);
    try {
      await createOrUpdateGroup();
      navigate('../learning-objectives');
    } catch (err) {
      console.error(err);
      toast.error('The Mentorship program could not be built. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AIStepLayout
      stepTitle="Program name"
      steps={MENTORSHIP_STEPS}
      title="Program name"
      onBack={() => navigate(`/app/programdetails/${orgIdParam}/${coIdParam}/admin/overview`)}
      onNext={handleNext}
      nextDisabled={!name.trim() || saving}
    >
      <DosenInput
        label="Program name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name your mentorship program"
        maxChars={75}
        fullWidth
      />
    </AIStepLayout>
  );
}

export default MentorshipProgramName;
